import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import useSnackBar from '../../../../../components/Commons/SnackBar/useSnackBar';
import { useGridPreferences } from '../../../../../hooks/useGridPreferences';
import { usePreferenceTypesStore } from '../../../../../store/Preferences/PreferencesStore';
import { useCompanyStore } from '../../../../../store/AuthStore';
import { GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { Section } from '../../../../../interfaces/Stocks/Warehouses/sections';
import { PreferenceType } from '../../../../../interfaces/Commons/Preferences/preferences';
import { Box } from '@mui/material';
import DataTableSticky from '../../../../../components/Tables/GridTableMaterialUI/DataTableSticky';
import { Header } from '../../../../../components/Header';
import { ErrorDisplay } from '../../../../../components/Commons/Error/ErrorDisplay';
import { Spinner } from '../../../../../components/Commons/Spinner/Spinner';
import { PrimaryButton } from '../../../../../theme/buttons';
import { FilterTable } from '../../../../../components/Filter';
import GenericPromises from '../../../../../api/GenericPromises';
import { useColumnsSections } from './columns';

export const TableSections = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const {
    LoadColumnsOrder,
    LoadColumnsVisible,
    LoadColumnsSort,
    VerifyPreferenceOrderByResourceRoute,
    VerifyPreferenceVisualByResourceRoute,
    VerifyPreferenceSortByResourceRoute,
  } = useGridPreferences();
  const { preferenceTypes } = usePreferenceTypesStore.getState();
  const { columnsSections } = useColumnsSections();
  const { companyState } = useCompanyStore();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isLoadingDataTable, setIsLoadingDataTable] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoadedMyLocalPreferences, setIsLoadedMyLocalPreferences] = useState(false);
  const { GenericGetResource } = GenericPromises();
  const [myPreferences, setMyPreferences] = useState({});
  const [mySortPreference, setMySortPreference] = useState([]);
  const [sectionsData, setSectionsData] = useState<GridRowsProp>([]);
  const [columns, setColumns] = useState<GridColDef<Section>[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const initFilter = '/sections?filter=';
  const [isLoadingData, setIsLoadingData] = useState(true);

  const myCustomColumns: GridColDef<any>[] = [];

  const loadTablePreferences = async () => {
    // get preferencetypes in Store
    let hasPreferenceOrder: boolean = false;
    // if exists preferencetypes
    if (preferenceTypes.length) {
      // if exists order preferences
      let myPreferencesColumnsOrder = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsOrder");
      if (myPreferencesColumnsOrder) {
        try {
          const preference = await VerifyPreferenceOrderByResourceRoute(
            "/sections",
            myPreferencesColumnsOrder.preference_type_id ?? 0
          );
          if (preference) {
            let myColumns = await LoadColumnsOrder("/sections", myCustomColumns, preference?.preference_value);
            setColumns(myColumns);
            hasPreferenceOrder = true;
          }
        } catch (error) {
          showSnackBar(`Error fetching column order preferences:${error}`, "error");
        }
      }

      let myPreferencesColumnsVisual = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsVisibility");
      // if exists visual preferences
      if (myPreferencesColumnsVisual) {
        try {
          const preference = await VerifyPreferenceVisualByResourceRoute(
            "/sections",
            myPreferencesColumnsVisual.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsVisible("/sections", preference);
            setMyPreferences(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column visual preferences:", error);
        }
      }

      let myPreferencesColumnsSort = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsSorting");
      // if exists sort preferences
      if (myPreferencesColumnsSort) {
        try {
          const preference = await VerifyPreferenceSortByResourceRoute(
            "/sections",
            myPreferencesColumnsSort.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsSort("/sections", preference);
            setMySortPreference(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column sort preferences:", error);
        }
      }

    }
    if (!hasPreferenceOrder) {
      setColumns(columnsSections);
    }
    setIsLoadedMyLocalPreferences(true);
  }

  const GetSections = () => {
    GenericGetResource(`/sections`)
    .then((response) => {
      setSectionsData(response.data.data);
      setIsLoadingData(false);
    });
  }

  useEffect(() => {
    if (isLoadedMyLocalPreferences && sectionsData) {
      setDataLoaded(true);
      setIsLoadingDataTable(true);
    }
  }, [sectionsData, isLoadedMyLocalPreferences]);

  useEffect(() => {
    document.title = `${companyState?.comercial_name} - ${t("sections.title")}`;
    const fetchData = async () => {
      try {
        await loadTablePreferences();
        GetSections();
      } catch (err: any) {
        setErrorMessage(err);
        setHasError(true);
        showSnackBar(err, 'error');
      }
    };

    fetchData();
  }, []);

  return (
    <>
      {!dataLoaded && !hasError && <Spinner />}
      {hasError &&
        <ErrorDisplay
          errorMessage={errorMessage}
        />
      }
      {dataLoaded && !hasError &&
        <>
          <Header
            size="sm"
            title={t("sections.title")}
            child={
              <Box display={"flex"} flexDirection={"row-reverse"}>
                <PrimaryButton
                  variant='outlined'
                  onClick={() => { navigate("add", { state: { ...location.state } }) }}
                  size="small"
                >
                  {t("generic.buttons.add")}
                </PrimaryButton>
                <FilterTable
                  onFilterEmpty={GetSections}
                  setIsLoadingDataTable={setIsLoadingData}
                  setDataTable={setSectionsData}
                  showSnackBar={showSnackBar}
                  columnsTable={columns}
                  initFilter={initFilter}
                  resourceRoute="/sections"
                />
              </Box>
            }
          />
          {isLoadingDataTable &&
            <Box sx={{ height: 'calc(100vh - 180px)', width: '100%' }}>
              <DataTableSticky
                columns={columns}
                setColumns={setColumns}
                data={sectionsData ?? []}
                entityId={"section_id"}
                entity={`Sections`}
                preferences={myPreferences}
                namePreferences={"/sections"}
                nameOrderColumns={"/sections"}
                sortPreference={mySortPreference}
                isLoading={isLoadingData}
              />
            </Box>
          }
        </>
      }
      <SnackbarComponent />
    </>
  )
}
